import { useCallback, useMemo } from "react";
import { useStoredState } from "./storage";

export const FAV_KEY = "favorites";

// Favorites can come from several places (Home cards, Category meals, Spoonacular),
// so every stored item is normalised to { id, title, image, isMealDB, ...raw }.
export const normalizeFav = (item) => ({
  ...item,
  id: String(item.id ?? item.idMeal),
  title: item.title || item.strMeal,
  image: item.image || item.strMealThumb,
  isMealDB: item.isMealDB ?? !!item.idMeal,
});

export function useFavorites() {
  const [raw, setRaw] = useStoredState(FAV_KEY, []);
  const favorites = useMemo(() => (Array.isArray(raw) ? raw.map(normalizeFav) : []), [raw]);
  const ids = useMemo(() => new Set(favorites.map((f) => f.id)), [favorites]);

  const isFavorite = useCallback((id) => ids.has(String(id)), [ids]);

  const addFavorite = useCallback(
    (item) => {
      const fav = normalizeFav(item);
      setRaw((prev) => {
        const list = (Array.isArray(prev) ? prev : []).map(normalizeFav);
        return list.some((f) => f.id === fav.id) ? list : [...list, fav];
      });
    },
    [setRaw]
  );

  const removeFavorite = useCallback(
    (id) => {
      setRaw((prev) =>
        (Array.isArray(prev) ? prev : []).map(normalizeFav).filter((f) => f.id !== String(id))
      );
    },
    [setRaw]
  );

  const removeMany = useCallback(
    (idList) => {
      const drop = new Set(idList.map(String));
      setRaw((prev) =>
        (Array.isArray(prev) ? prev : []).map(normalizeFav).filter((f) => !drop.has(f.id))
      );
    },
    [setRaw]
  );

  const replaceAll = useCallback((list) => setRaw(list), [setRaw]);

  return { favorites, isFavorite, addFavorite, removeFavorite, removeMany, replaceAll };
}
